/**
 * PAC-1374 — country and currency pick-lists for the company sign-up form.
 *
 * Built from the browser's own Intl data rather than a hand-kept list: the
 * visitor has no organisation yet, so the platform's authenticated reference
 * endpoints are not reachable, and a pasted list of ~250 codes would rot.
 */
export interface PickOption {
  value: string;
  title: string;
}

/** Codes Intl names that are not countries a company can be registered in. */
const NON_COUNTRY_REGIONS: ReadonlySet<string> = new Set(["EU", "EZ", "UN", "QO", "XA", "XB", "ZZ"]);
const A_CODE = 65;
const LETTERS = 26;

function displayNames(locale: string, type: "region" | "currency"): Intl.DisplayNames | null {
  try {
    return new Intl.DisplayNames([locale], { type, fallback: "none" } as Intl.DisplayNamesOptions);
  } catch {
    return null;
  }
}

const byTitle = (a: PickOption, b: PickOption) => a.title.localeCompare(b.title);

/** Every ISO 3166-1 alpha-2 code Intl knows a name for, labelled in `locale`. */
export function countryOptions(locale: string): PickOption[] {
  const names = displayNames(locale, "region");
  if (!names) return [];
  const out: PickOption[] = [];
  for (let i = 0; i < LETTERS; i++) {
    for (let j = 0; j < LETTERS; j++) {
      const code = String.fromCharCode(A_CODE + i, A_CODE + j);
      if (NON_COUNTRY_REGIONS.has(code)) continue;
      const title = names.of(code);
      if (title && title !== code) out.push({ value: code, title });
    }
  }
  return out.sort(byTitle);
}

/** Every ISO 4217 currency the browser supports, "ZAR — South African Rand". */
export function currencyOptions(locale: string): PickOption[] {
  const supported = (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf;
  const codes = typeof supported === "function" ? supported("currency") : [];
  const names = displayNames(locale, "currency");
  return codes
    .map((code) => {
      const name = names?.of(code);
      return { value: code, title: name && name !== code ? `${code} — ${name}` : code };
    })
    .sort((a, b) => a.value.localeCompare(b.value));
}
